import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  fetchAttendanceSheet,
  fetchMeetings,
  fetchCurriculum,
  markAttendance,
  markAttendanceBulk,
} from "./adminCourseService";
import "./adminCourses.css";

/* ── status config ── */
const STATUS_DOT = {
  ATTENDED:    { color: "#16a34a", label: "Attended",    emoji: "●" },
  ABSENT:      { color: "#dc2626", label: "Absent",      emoji: "●" },
  MISSED:      { color: "#f59e0b", label: "Missed",      emoji: "●" },
  SCHEDULED:   { color: "#93c5fd", label: "Scheduled",   emoji: "●" },
  RESCHEDULED: { color: "#fef08a", label: "Rescheduled", emoji: "●" },
  CANCELLED:   { color: "#9ca3af", label: "Cancelled",   emoji: "●" },
};

/* ── cell component ── */
const AttendanceCell = ({ status, onMark, disabled }) => {
  const [pos, setPos] = useState(null);
  const tdRef = useRef(null);
  const dot = STATUS_DOT[status];

  const handleClick = () => {
    if (disabled) return;
    if (pos) { setPos(null); return; }
    const rect = tdRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
  };

  return (
    <td
      ref={tdRef}
      style={{ textAlign: "center", cursor: disabled ? "default" : "pointer" }}
      onClick={handleClick}
      title={dot?.label || "Not scheduled"}
    >
      <span style={{ fontSize: "1rem", color: dot?.color || "#e5e7eb" }}>
        {dot ? dot.emoji : "○"}
      </span>

      {pos && createPortal(
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => setPos(null)} />
          <div style={{
            position: "absolute", top: pos.top + 4, left: pos.left,
            background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 9999,
            minWidth: 140, overflow: "hidden",
          }}>
            {["ATTENDED", "ABSENT", "RESCHEDULED", "CANCELLED"].map((s) => (
              <div
                key={s}
                onClick={(e) => { e.stopPropagation(); onMark(s); setPos(null); }}
                style={{
                  padding: "0.5rem 0.9rem", fontSize: "0.82rem", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "0.5rem",
                  background: status === s ? "#f0f9ff" : "#fff",
                  fontWeight: status === s ? 600 : 400,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                onMouseLeave={(e) => e.currentTarget.style.background = status === s ? "#f0f9ff" : "#fff"}
              >
                <span style={{ color: STATUS_DOT[s]?.color }}>{STATUS_DOT[s]?.emoji}</span>
                {STATUS_DOT[s]?.label}
              </div>
            ))}
          </div>
        </>,
        document.body
      )}
    </td>
  );
};

/* ── bulk mark modal ── */
const BulkMarkModal = ({ meetings, curriculum, onMark, onClose, saving }) => {
  const [meetingId, setMeetingId] = useState("");
  const [status, setStatus] = useState("ATTENDED");

  const getMeetingLabel = (m) => {
    const lesson = curriculum.find((c) => c.id === m.curriculumId);
    const day = lesson ? `Day ${lesson.dayNumber}` : `Meeting #${m.id}`;
    return `${day} — ${m.scheduledAt ? new Date(m.scheduledAt).toLocaleDateString("en-IN") : ""}`;
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }}>
      <div style={{
        background: "#fff", borderRadius: 10, padding: "1.5rem",
        width: "100%", maxWidth: 400, boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
      }}>
        <h3 style={{ margin: "0 0 1rem", fontSize: "1.1rem" }}>Bulk Mark Attendance</h3>

        <div className="ac-form-group">
          <label>Meeting *</label>
          <select value={meetingId} onChange={(e) => setMeetingId(e.target.value)} required>
            <option value="">— Select meeting —</option>
            {meetings
              .filter((m) => m.status !== "CANCELLED" && m.status !== "RESCHEDULED")
              .map((m) => (
                <option key={m.id} value={m.id}>{getMeetingLabel(m)}</option>
              ))}
          </select>
        </div>

        <div className="ac-form-group">
          <label>Mark all students as</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ATTENDED">Attended</option>
            <option value="ABSENT">Absent</option>
            <option value="RESCHEDULED">Rescheduled</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="ac-form-actions">
          <button
            className="ac-btn ac-btn-success"
            disabled={!meetingId || saving}
            onClick={() => onMark(Number(meetingId), status)}
          >
            {saving ? "Marking…" : "Mark All"}
          </button>
          <button className="ac-btn ac-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ── CSV export ── */
const exportCSV = (students, days, grid) => {
  const header = ["Student", ...days.map((d) => `Day ${d.dayNumber}`)].join(",");
  const rows = students.map((s) => {
    const cells = days.map((d) => {
      const key = `${s.enrollmentId}_${d.id}`;
      return grid[key] || "";
    });
    return [s.userName, ...cells].join(",");
  });
  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "attendance.csv";
  a.click();
  URL.revokeObjectURL(url);
};

/* ─────────────────────────────────────────────
   Main tab component
───────────────────────────────────────────── */
const AdminAttendanceTab = ({ courseId, batchId, students }) => {
  const [grid, setGrid]           = useState({});   // key: `${enrollmentId}_${curriculumId}` → status
  const [curriculum, setCurriculum] = useState([]);
  const [meetings, setMeetings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [showBulk, setShowBulk]   = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [marking, setMarking]     = useState(null); // `${enrollmentId}_${curriculumId}`

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [attRes, currRes, meetRes] = await Promise.all([
        fetchAttendanceSheet(courseId, batchId),
        fetchCurriculum(courseId),
        fetchMeetings(courseId, batchId),
      ]);

      // build grid from attendance records
      const newGrid = {};
      (attRes.data || []).forEach((record) => {
        const key = `${record.enrollmentId}_${record.curriculumId}`;
        newGrid[key] = record.status;
      });
      setGrid(newGrid);

      const sortedCurr = (currRes.data || []).sort((a, b) => a.dayNumber - b.dayNumber);
      setCurriculum(sortedCurr);
      setMeetings(meetRes.data || []);
    } catch {
      setError("Failed to load attendance.");
    } finally {
      setLoading(false);
    }
  }, [courseId, batchId]);

  useEffect(() => { load(); }, [load]);

  /* ── mark single cell ── */
  const handleMark = async (enrollmentId, curriculumId, status) => {
    const key = `${enrollmentId}_${curriculumId}`;
    setMarking(key);
    setError("");
    // optimistic update
    setGrid((prev) => ({ ...prev, [key]: status }));
    try {
      await markAttendance(courseId, batchId, { enrollmentId, curriculumId, status });
    } catch (err) {
      // revert on failure
      await load();
      const msg = err?.response?.data?.message || err?.response?.data || "Mark failed.";
      setError(String(msg));
    } finally {
      setMarking(null);
    }
  };

  /* ── bulk mark ── */
  const handleBulkMark = async (meetingId, status) => {
    setBulkSaving(true);
    setError("");
    try {
      const meeting = meetings.find((m) => m.id === meetingId);
      if (!meeting) throw new Error("Meeting not found");

      const records = (students || [])
        .filter((s) => s.status === "ACTIVE")
        .map((s) => ({
          enrollmentId: s.enrollmentId,
          curriculumId: meeting.curriculumId,
          status,
        }));

      await markAttendanceBulk(courseId, batchId, { records });
      setShowBulk(false);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Bulk mark failed.";
      setError(String(msg));
    } finally {
      setBulkSaving(false);
    }
  };

  if (loading) return <div className="ac-loading">Loading attendance…</div>;

  const activeStudents = (students || []).filter((s) => s.status !== "CANCELLED");

  return (
    <div>
      {/* ── toolbar ── */}
      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
        <button className="ac-btn ac-btn-primary" onClick={() => setShowBulk(true)}>
          Bulk Mark
        </button>
        <button
          className="ac-btn ac-btn-ghost"
          onClick={() => exportCSV(activeStudents, curriculum, grid)}
        >
          Export CSV
        </button>
      </div>

      {error && (
        <div style={{
          background: "#fee2e2", color: "#b91c1c",
          padding: "0.6rem 0.9rem", borderRadius: "6px",
          marginBottom: "1rem", fontSize: "0.88rem",
        }}>
          {error}
        </div>
      )}

      {/* ── legend ── */}
      <div style={{ display: "flex", gap: "1rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        {Object.entries(STATUS_DOT).map(([s, cfg]) => (
          <div key={s} style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.8rem", color: "#6b7280" }}>
            <span style={{ color: cfg.color, fontSize: "1rem" }}>{cfg.emoji}</span>
            {cfg.label}
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.8rem", color: "#6b7280" }}>
          <span style={{ color: "#e5e7eb", fontSize: "1rem" }}>○</span>
          Not scheduled
        </div>
      </div>

      {activeStudents.length === 0 ? (
        <div className="ac-empty">No active students enrolled.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="ac-table" style={{ minWidth: curriculum.length * 52 + 180 }}>
            <thead>
              <tr>
                <th style={{ minWidth: 160, position: "sticky", left: 0, background: "#f9fafb", zIndex: 2 }}>
                  Student
                </th>
                {curriculum.map((c) => (
                  <th
                    key={c.id}
                    style={{ textAlign: "center", minWidth: 48, fontSize: "0.78rem", padding: "0.5rem 0.25rem" }}
                    title={`Day ${c.dayNumber}: ${c.title}`}
                  >
                    <div>D{c.dayNumber}</div>
                    {c.isAssessment && (
                      <div style={{ color: "#7c3aed", fontSize: "0.65rem" }}>★</div>
                    )}
                  </th>
                ))}
                <th style={{ textAlign: "center", minWidth: 60, fontSize: "0.78rem" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {activeStudents.map((s) => {
                const attended = curriculum.filter(
                  (c) => grid[`${s.enrollmentId}_${c.id}`] === "ATTENDED"
                ).length;

                return (
                  <tr key={s.enrollmentId}>
                    <td style={{
                      position: "sticky", left: 0, background: "#fff", zIndex: 1,
                      fontWeight: 500, fontSize: "0.85rem",
                    }}>
                      {s.userName}
                      {s.adminGranted && (
                        <span className="ac-badge ac-badge-orange" style={{ fontSize: "0.65rem", marginLeft: "0.3rem" }}>
                          Admin
                        </span>
                      )}
                    </td>
                    {curriculum.map((c) => {
                      const key = `${s.enrollmentId}_${c.id}`;
                      return (
                        <AttendanceCell
                          key={c.id}
                          status={grid[key]}
                          disabled={marking === key}
                          onMark={(status) => handleMark(s.enrollmentId, c.id, status)}
                        />
                      );
                    })}
                    <td style={{ textAlign: "center", fontSize: "0.82rem", fontWeight: 600, color: "#16a34a" }}>
                      {attended}/{curriculum.length}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showBulk && (
        <BulkMarkModal
          meetings={meetings}
          curriculum={curriculum}
          onMark={handleBulkMark}
          onClose={() => setShowBulk(false)}
          saving={bulkSaving}
        />
      )}
    </div>
  );
};

export default AdminAttendanceTab;
